import React, { createContext, useContext, useRef, useCallback, useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";

interface ContentNavigationContextValue {
  navigateToPage: (page: number) => void;
  navigateToTimestamp: (seconds: number) => void;
  registerPdfNavigation: (fn: ((page: number) => void) | null) => void;
  registerMediaNavigation: (fn: ((seconds: number) => void) | null) => void;
  initialPage: number | null;
  clearInitialPage: () => void;
  // Cross-panel selection action callbacks
  sendChatMessage: (text: string) => void;
  setChatReplyText: (text: string) => void;
  switchToTab: (tab: string) => void;
  addToNotes: (text: string) => void;
  registerChatActions: (actions: {
    sendMessage: (text: string) => void;
    setReplyText: (text: string) => void;
  } | null) => void;
  registerTabSwitcher: (fn: ((tab: string) => void) | null) => void;
  registerNotesAction: (fn: ((text: string) => void) | null) => void;
}

const ContentNavigationContext = createContext<ContentNavigationContextValue | null>(null);

export function ContentNavigationProvider({ children }: { children: React.ReactNode }) {
  const [searchParams] = useSearchParams();
  const pdfNavigationRef = useRef<((page: number) => void) | null>(null);
  const mediaNavigationRef = useRef<((seconds: number) => void) | null>(null);
  const chatActionsRef = useRef<{ sendMessage: (text: string) => void; setReplyText: (text: string) => void } | null>(null);
  const tabSwitcherRef = useRef<((tab: string) => void) | null>(null);
  const notesActionRef = useRef<((text: string) => void) | null>(null);
  
  const pageParam = searchParams.get('page');
  const [initialPage, setInitialPage] = useState<number | null>(
    pageParam ? parseInt(pageParam, 10) : null
  );
  const [hasNavigated, setHasNavigated] = useState(false);

  const clearInitialPage = useCallback(() => {
    setInitialPage(null);
    setHasNavigated(true);
  }, []);

  const registerPdfNavigation = useCallback((fn: ((page: number) => void) | null) => {
    pdfNavigationRef.current = fn;
    if (fn && initialPage && !hasNavigated) {
      setTimeout(() => {
        fn(initialPage);
        setHasNavigated(true);
      }, 300);
    }
  }, [initialPage, hasNavigated]);

  const registerMediaNavigation = useCallback((fn: ((seconds: number) => void) | null) => {
    mediaNavigationRef.current = fn;
  }, []);

  const registerChatActions = useCallback((actions: { sendMessage: (text: string) => void; setReplyText: (text: string) => void } | null) => {
    chatActionsRef.current = actions;
  }, []);

  const registerTabSwitcher = useCallback((fn: ((tab: string) => void) | null) => {
    tabSwitcherRef.current = fn;
  }, []);

  const registerNotesAction = useCallback((fn: ((text: string) => void) | null) => {
    notesActionRef.current = fn;
  }, []);

  const navigateToPage = useCallback((page: number) => {
    pdfNavigationRef.current?.(page);
  }, []);

  const navigateToTimestamp = useCallback((seconds: number) => {
    mediaNavigationRef.current?.(seconds);
  }, []);

  const sendChatMessage = useCallback((text: string) => {
    chatActionsRef.current?.sendMessage(text);
  }, []);

  const setChatReplyText = useCallback((text: string) => {
    chatActionsRef.current?.setReplyText(text);
  }, []);

  const switchToTab = useCallback((tab: string) => {
    tabSwitcherRef.current?.(tab);
  }, []);

  const addToNotes = useCallback((text: string) => {
    notesActionRef.current?.(text);
  }, []);

  useEffect(() => {
    const newPage = pageParam ? parseInt(pageParam, 10) : null;
    if (newPage !== initialPage) {
      setInitialPage(newPage);
      setHasNavigated(false);
    }
  }, [pageParam]);

  return (
    <ContentNavigationContext.Provider
      value={{
        navigateToPage,
        navigateToTimestamp,
        registerPdfNavigation,
        registerMediaNavigation,
        initialPage,
        clearInitialPage,
        sendChatMessage,
        setChatReplyText,
        switchToTab,
        addToNotes,
        registerChatActions,
        registerTabSwitcher,
        registerNotesAction,
      }}
    >
      {children}
    </ContentNavigationContext.Provider>
  );
}

export function useContentNavigation() {
  const context = useContext(ContentNavigationContext);
  if (!context) {
    throw new Error("useContentNavigation must be used within ContentNavigationProvider");
  }
  return context;
}

export function useContentNavigationOptional() {
  return useContext(ContentNavigationContext);
}
